import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { activeOrganizationId, graphql, initializeActiveOrganizationId } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { config } from '../config';

type Session = { user: { displayName: string; email: string; internal: boolean }; memberships: Array<{ organizationId: string; organizationName: string; role: string }> };

export function RequireAuth({ operator = false }: { operator?: boolean }) {
  const auth = useAuth();
  const currentLocation = useLocation();
  if (auth.loading) return <div className="loading-screen" role="status">Loading PayRight…</div>;
  if (!auth.identity) return <Navigate to="/login" replace state={{ returnTo: `${currentLocation.pathname}${currentLocation.search}` }} />;
  if (operator && !auth.identity.internal) return <Navigate to="/app" replace />;
  return <Outlet />;
}

const customerLinks = [['/app', 'Jobs'], ['/app/projects/new', 'Add a job'], ['/app/orders', 'Notices'], ['/app/documents', 'Proof & files'], ['/app/team', 'People & access'], ['/app/reports', 'Summary'], ['/app/billing', 'Plan & billing'], ['/app/settings', 'Notifications'], ['/app/help', 'Help & workflows']];
const operatorLinks = [['/ops', 'Notice queue'], ['/ops/rules', 'Date rules'], ['/ops/exceptions', 'Problems'], ['/ops/support', 'Customer help'], ['/ops/quality', 'Review quality'], ['/ops/metrics', 'Team results'], ['/ops/help', 'Help & workflows']];

export function WorkspaceLayout({ operator = false }: { operator?: boolean }) {
  const auth = useAuth();
  const [session, setSession] = useState<Session | null>(null);
  const [error, setError] = useState('');
  const [invitationError, setInvitationError] = useState('');
  const [sessionLoading, setSessionLoading] = useState(true);
  const bootstrapStarted = useRef(false);
  useEffect(() => {
    if (!auth.token || bootstrapStarted.current) return;
    bootstrapStarted.current = true;
    const authToken = auth.token;
    const pendingInvitation = sessionStorage.getItem('payright-invitation-token');
    const acceptPending = pendingInvitation
      ? graphql<{ acceptInvitation: { organizationId: string } }>({
          query: 'mutation AcceptInvitation($token:String!){acceptInvitation(token:$token){organizationId organizationName role}}',
          variables: { token: pendingInvitation }, token: authToken
        }).then(({ acceptInvitation }) => {
          localStorage.setItem('payright-organization-id', acceptInvitation.organizationId);
          sessionStorage.removeItem('payright-invitation-token');
        }).catch((reason: Error) => {
          sessionStorage.removeItem('payright-invitation-token');
          setInvitationError(reason.message);
        })
      : Promise.resolve();
    void acceptPending.then(() => graphql<{ me: Session }>({ query: 'query { me { user { displayName email internal } memberships { organizationId organizationName role } } }', token: authToken }))
      .then(({ me }) => {
        if (initializeActiveOrganizationId(me.memberships.map((membership) => membership.organizationId))) {
          window.location.reload();
          return;
        }
        setSession(me);
        setSessionLoading(false);
      }).catch((reason: Error) => { setError(reason.message); setSessionLoading(false); });
  }, [auth.token]);
  const links = operator ? operatorLinks : customerLinks;
  function selectOrganization(organizationId: string) {
    localStorage.setItem('payright-organization-id', organizationId);
    window.location.reload();
  }
  return <div className="workspace">
    <aside className="workspace-nav">
      <Link className="wordmark" to="/">PayRight<span>.</span></Link>
      <div className="workspace-name"><small>{operator ? 'PayRight staff tools' : 'Notice workspace'}</small>{session && session.memberships.length > 1 ? <select aria-label="Company workspace" value={activeOrganizationId()} onChange={(event) => selectOrganization(event.target.value)}>{session.memberships.map((membership) => <option key={membership.organizationId} value={membership.organizationId}>{membership.organizationName}</option>)}</select> : <strong>{session?.memberships[0]?.organizationName ?? (sessionLoading ? 'Checking access…' : 'Access needed')}</strong>}</div>
      {session?.memberships.length ? <nav aria-label={operator ? 'Staff tools' : 'Notice workspace'}>{links.map(([to, label]) => <NavLink key={to} end={to === '/app' || to === '/ops'} to={to}>{label}</NavLink>)}</nav> : null}
      <div className="workspace-user"><span>{auth.identity?.name}</span><small>{auth.identity?.email}</small><button type="button" onClick={() => void auth.logout()}>Sign out</button>{!operator && auth.identity?.internal && <Link to="/ops">Open operations →</Link>}</div>
    </aside>
    <main className="workspace-main">{error ? <section className="empty-state" role="alert"><div className="eyebrow">Unable to open PayRight</div><h1>We could not check your access.</h1><p>{error}</p><p>Try signing out and back in. If the problem continues, ask a 5x5 administrator to check your PayRight access.</p></section> : sessionLoading ? <div className="loading-screen" role="status">Setting up your PayRight account…</div> : session && session.memberships.length === 0 ? <CompanyOnboarding invitationError={invitationError} /> : <Outlet />}</main>
  </div>;
}

function CompanyOnboarding({ invitationError }: { invitationError: string }) {
  const auth = useAuth();
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  async function createCompany(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!auth.token) return;
    setSaving(true); setError('');
    const name = String(new FormData(event.currentTarget).get('name') ?? '');
    try {
      const result = await graphql<{ createOrganization: { organizationId: string } }>({
        query: 'mutation CreateOrganization($name:String!){createOrganization(name:$name){organizationId organizationName role}}',
        variables: { name }, token: auth.token
      });
      localStorage.setItem('payright-organization-id', result.createOrganization.organizationId);
      window.location.assign('/app');
    } catch (reason) {
      setError((reason as Error).message); setSaving(false);
    }
  }
  return <section className="empty-state access-required onboarding-card">
    <div className="eyebrow">Account created · one quick step</div>
    <h1>What company will use PayRight?</h1>
    <p>Create your company workspace. You will be its owner and can invite teammates after you enter.</p>
    {invitationError && <div className="alert error" role="alert"><strong>That invitation could not be used.</strong><br />{invitationError}</div>}
    {error && <div className="alert error" role="alert">{error}</div>}
    <form className="onboarding-form" onSubmit={createCompany}>
      <label>Company name<input name="name" required minLength={2} maxLength={160} autoComplete="organization" placeholder="Example: Gulf Coast Supply" /></label>
      <button className="button" disabled={saving}>{saving ? 'Creating workspace…' : 'Create company workspace'}</button>
    </form>
    <p className="form-note">Joining an existing company instead? Ask its PayRight owner to send you an invitation link.</p>
    <small>Development environment: use fictional company and project information only.</small>
  </section>;
}

export function PageHeader({ eyebrow, title, action }: { eyebrow: string; title: string; action?: ReactNode }) {
  return <header className="workspace-header"><div><div className="eyebrow">{eyebrow}</div><h1>{title}</h1></div>{action}</header>;
}

export function LoginPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState('');
  const redirectStarted = useRef(false);
  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo ?? '/app';
  async function enter(mode: 'customer' | 'operator' | 'reviewer') { const staff = mode !== 'customer'; setError(''); try { await auth.login(mode, staff ? '/ops' : returnTo); if (config.authMode === 'development') navigate(staff ? '/ops' : returnTo, { replace: true }); } catch { setError('Secure sign in could not start. Please try again.'); } }
  useEffect(() => {
    if (config.authMode === 'development' || redirectStarted.current || auth.loading || auth.identity) return;
    redirectStarted.current = true;
    void enter('customer');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.loading, auth.identity]);
  if (!auth.loading && auth.identity) return <Navigate to={auth.identity.internal ? '/ops' : returnTo} replace />;
  if (config.authMode === 'development') return <main className="auth-page"><Link className="wordmark" to="/">PayRight<span>.</span></Link><section><div className="eyebrow">Local development workspace</div><h1>Continue to PayRight</h1><p>Choose which fictional workspace to open locally.</p><button className="button" onClick={() => void enter('customer')}>Enter as customer</button><button className="button button-quiet" onClick={() => void enter('operator')}>Enter as PayRight staff</button><button className="button button-quiet" onClick={() => void enter('reviewer')}>Enter as separate final reviewer</button><small>Local development tools. No real data here.</small>{error && <div className="alert error" role="alert">{error}</div>}</section></main>;
  return error ? <main className="auth-page"><Link className="wordmark" to="/">PayRight<span>.</span></Link><section role="alert"><div className="eyebrow">Sign-in problem</div><h1>We could not start sign-in.</h1><p>{error}</p><button className="button" onClick={() => void enter('customer')}>Try again</button></section></main> : <div className="loading-screen" role="status">Redirecting to secure sign-in…</div>;
}

export function SignupPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState('');
  const invitation = new URLSearchParams(location.search).get('invite');
  useEffect(() => { if (invitation) sessionStorage.setItem('payright-invitation-token', invitation); }, [invitation]);
  async function begin(kind: 'register' | 'login') {
    setError('');
    try {
      if (kind === 'register') await auth.register('/app'); else await auth.login('customer', '/app');
      if (config.authMode === 'development') navigate('/app', { replace: true });
    } catch { setError('Secure account setup could not start. Please try again.'); }
  }
  if (!auth.loading && auth.identity) return <Navigate to={auth.identity.internal ? '/ops' : '/app'} replace />;
  return <main className="auth-page"><Link className="wordmark" to="/">PayRight<span>.</span></Link><section><div className="eyebrow">{invitation ? 'Company invitation' : 'New PayRight account'}</div><h1>{invitation ? 'Join your teammate in PayRight.' : 'Start your company workspace.'}</h1><p>{invitation ? 'Create a secure account with the same email address that received the invitation. PayRight will add you to the company automatically.' : 'Create your secure account, then enter your company name. You can add jobs and invite teammates right away.'}</p><button className="button" onClick={() => void begin('register')}>{invitation ? 'Create account and join' : 'Create my account'}</button><button className="button button-quiet" onClick={() => void begin('login')}>{invitation ? 'I already have an account' : 'Sign in to an existing account'}</button><Link className="text-link" to="/help">See how account setup works</Link>{error && <div className="alert error" role="alert">{error}</div>}</section></main>;
}

export function AuthCallbackPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const callbackStarted = useRef(false);
  const { completeLogin } = auth;
  useEffect(() => {
    if (callbackStarted.current) return;
    callbackStarted.current = true;
    void completeLogin().then((returnTo) => navigate(returnTo, { replace: true })).catch(() => setError('The secure sign-in response could not be completed. It may have expired or already been used.'));
  }, [completeLogin, navigate]);
  return error ? <main className="auth-page"><Link className="wordmark" to="/">PayRight<span>.</span></Link><section role="alert"><div className="eyebrow">Sign-in problem</div><h1>We could not finish signing you in.</h1><p>{error}</p><Link className="button" to="/login">Try secure sign in again</Link></section></main> : <div className="loading-screen" role="status">Completing secure sign in…</div>;
}
